import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import Recaptcha from 'react-google-recaptcha';
import {generateUniqKey} from 'helpers/uniq';
import {invisibleRecaptchaConfig} from 'modules/forms/constants/invisibleRecaptcha';
import {LoadRecaptcha} from 'modules/forms/components/LoadRecaptcha';

const RecaptchaContext = React.createContext(null);

export const RecaptchaProvider = ({ children }) => {
	const recaptchaRef = useRef(null);
	const [reloadKey, setReloadKey] = useState(0);
	const sitekey = invisibleRecaptchaConfig.key;

	const [state, setState] = useState({
		isReady: false,
		isExpired: false,
		token: null,
	});

	const executeRecaptcha = useCallback(async () => {
		if (typeof window === 'undefined') {
			return 'dummy-token-for-ssr';
		}

		if (!window.grecaptcha) {
			return null;
		}

		const token = await recaptchaRef.current?.executeAsync();
		setState((prev) => ({ ...prev, token }));
		return token;
	});

	const executeCaptcha = useCallback(async () => {
		if (typeof window === 'undefined') {
			return 'dummy-token-for-ssr';
		}

		if (!window.grecaptcha) {
			return null;
		}
		const token = await recaptchaRef.current?.executeAsync();
		setState((prev) => ({ ...prev, token }));
		return token;
	});

	const handleLoad = () => {
		setState((prev) => ({ ...prev, isReady: true, isExpired: false, token: null }));
	};

	const handleVerify = (response) => {
		setState((prev) => ({ ...prev, token: response }));
	};

	const resetCaptcha = () => {
		setState((prev) => ({ ...prev, isReady: false }));
		setTimeout(() => {
			setReloadKey((prev) => prev + 1);
		}, 100);
	};

	const handleExpired = () => {
		setState((prev) => ({ ...prev, token: null, isReady: true, isExpired: true }));
	};

	useEffect(() => {
		const moveBadge = () => {
			const badge = document.querySelector('.grecaptcha-badge');
			if (badge) {
				if (badge.parentElement !== document.body) {
					document.body.appendChild(badge);
				}
				badge.style.zIndex = '9999';
			}
		};
		const interval = setInterval(moveBadge, 100);

		return () => clearInterval(interval);
	}, []);

	return (
		<RecaptchaContext.Provider
			value={{
				resetCaptcha,
				executeRecaptcha,
				executeCaptcha,
				token: state.token,
				isReady: state.isReady,
				isExpired: state.isExpired,
			}}
		>
			<LoadRecaptcha>
				<Recaptcha
					key={`recaptcha-${reloadKey}`}
					ref={recaptchaRef}
					elementID={`g-recaptcha${generateUniqKey()}`}
					sitekey={sitekey}
					size={invisibleRecaptchaConfig.size}
					render={invisibleRecaptchaConfig.render}
					onChange={handleVerify}
					onExpired={handleExpired}
					asyncScriptOnLoad={handleLoad}
					badge="bottomright"
					container="body"
				/>
			</LoadRecaptcha>
			{children}
		</RecaptchaContext.Provider>
	);
};

export const useRecaptcha = () => {
	const context = useContext(RecaptchaContext);
	if (typeof window === 'undefined' || !context) {
		return {
			executeCaptcha: () => Promise.resolve('dummy-token-ssr'),
			executeRecaptcha: () => Promise.resolve('dummy-token-ssr'),
			token: null,
			isReady: false,
			isExpired: false,
			resetCaptcha: () => {},
		};
	}
	return context;
};
